import React, { useEffect, useState } from "react";
import defaultSellerAvatar from "../../assets/profile.png";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import { IoBagHandleOutline, IoChevronBack, IoChevronForward, IoAdd, IoRemove } from "react-icons/io5";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { addToCart } from "../../api/cartApi";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
// The cart API accepts 1-10 units of a product per add
const MAX_CART_QUANTITY = 10;

const ProductView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const stateProduct = location.state?.product;

  const [product, setProduct] = useState(stateProduct || null);
  const [activeImage, setActiveImage] = useState(0);
  const [loading, setLoading] = useState(!stateProduct);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  // A product passed from search renders at once; stock and price are always reloaded from the API
  useEffect(() => {
    let active = true;
    setActiveImage(0);
    setQuantity(1);
    setError(null);
    if (!stateProduct) setLoading(true);

    const token = sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");
    if (!token) {
      setError("Please log in to view this product.");
      setLoading(false);
      return undefined;
    }

    axios
      .get(`${API_BASE_URL}/product`, { params: { id }, headers: { token } })
      .then((response) => {
        if (!active) return;
        if (response.data?.success && response.data.result) {
          setProduct(response.data.result);
        } else {
          setError(response.data?.message || "Failed to fetch product");
        }
      })
      .catch((err) => {
        if (active) setError(err.response?.data?.message || "Error fetching product");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [id, stateProduct]);

  if (loading) return <div className="p-6 text-gray-600">Loading product details...</div>;
  if (!product) return <div className="p-6 text-red-600">{error || "No product found."}</div>;

  const images = (product.product_media || []).filter((media) => media?.url);
  const currentImage = images[Math.min(activeImage, images.length - 1)];
  const showImage = (index) => setActiveImage((index + images.length) % images.length);

  const price = Number(product.product_price) || 0;
  const discountPrice = Number(product.discount_price) || price;
  const currencySymbol = product.seller?.currency_symbol || "$";
  const hasDiscount = discountPrice < price;
  const discountPercentage = hasDiscount ? Math.round(((price - discountPrice) / price) * 100) : 0;

  const stock = Math.max(0, Number(product.product_qty) || 0);
  const isAvailable = !product.is_deleted && product.status !== "Inactive";
  // Only sellers approved by admin take orders (the cart API enforces it too); unknown until the product detail loads
  const sellerStatus = product.seller?.seller_status;
  const sellerAcceptingOrders = sellerStatus === undefined || String(sellerStatus || "").toLowerCase() === "approved";
  const canBuy = isAvailable && sellerAcceptingOrders && stock > 0;
  const maxQuantity = Math.min(stock, MAX_CART_QUANTITY);

  const handleBuyNow = async () => {
    if (!canBuy || adding) return;
    setAdding(true);
    try {
      const response = await addToCart(product.id, quantity);
      if (response?.success) {
        navigate("/cart");
      } else {
        alert(response?.message || "Failed to add to cart.");
      }
    } catch (err) {
      alert("Something went wrong while adding to cart.");
    } finally {
      setAdding(false);
    }
  };

  const priceLabel = "font-medium italic text-[12px] text-[#161F28] mb-2";

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />

        <div className="mb-6">
          <button className="text-sm text-[#095228] font-semibold" onClick={() => navigate(-1)}>
            ← BACK
          </button>
        </div>

        {error && <div className="mb-4 text-sm text-red-600">{error}</div>}

        <div className="flex flex-col md:flex-row gap-8 lg:gap-12 bg-white p-4 md:p-6">
          <div className="w-full md:w-[42%] flex flex-col gap-4">
            <div className="relative aspect-square rounded-3xl bg-[#F1F1F1] flex items-center justify-center overflow-hidden">
              {currentImage ? (
                <img src={currentImage.url} alt={product.product_name} className="w-full h-full object-contain p-6" />
              ) : (
                <span className="text-gray-500">No image available</span>
              )}
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    aria-label="Previous image"
                    onClick={() => showImage(activeImage - 1)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white"
                  >
                    <IoChevronBack />
                  </button>
                  <button
                    type="button"
                    aria-label="Next image"
                    onClick={() => showImage(activeImage + 1)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white"
                  >
                    <IoChevronForward />
                  </button>
                </>
              )}
            </div>

            {images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-1">
                {images.map((media, index) => (
                  <button
                    type="button"
                    key={`${media.url}-${index}`}
                    aria-label={`Show image ${index + 1}`}
                    onClick={() => setActiveImage(index)}
                    className={`shrink-0 w-[calc((100%-1.5rem)/3)] aspect-[4/3] rounded-2xl bg-[#F1F1F1] border-2 overflow-hidden ${
                      index === activeImage ? "border-[#095228]" : "border-transparent"
                    }`}
                  >
                    <img src={media.url} alt="" className="w-full h-full object-contain p-2" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-full md:w-[58%] flex flex-col">
            {(product.category?.title || product.subcategory?.title) && (
              <p className="text-[15px] font-semibold italic text-[#161F28] mb-3">
                {[product.category?.title, product.subcategory?.title].filter(Boolean).join(" → ")}
              </p>
            )}

            <div className="flex flex-wrap gap-3 items-center mb-3">
              {product.brand_name && (
                <>
                  <p className="text-[#095228] font-bold uppercase">{product.brand_name}</p>
                  <span className="border-l border-gray-400 h-5" />
                </>
              )}
              <div className="flex gap-2 items-center text-[13px] font-semibold text-[#095228]">
                Sold By:
                <span className="flex gap-1 items-center border border-[#08AEEA] rounded-lg px-2 py-0.5">
                  <img
                    src={product.seller?.profile_image?.url || (typeof product.seller?.profile_image === "string" ? product.seller.profile_image : "") || defaultSellerAvatar}
                    alt=""
                    className="w-4 h-4 rounded-full object-cover"
                  />
                  <span className="text-[10px] font-semibold text-[#161F28]">{product.seller?.name || "Seller"}</span>
                </span>
              </div>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold leading-snug text-[#161F28] mb-6">{product.product_name}</h1>

            <div className="flex flex-wrap items-end gap-x-14 gap-y-4 mb-6">
              {hasDiscount && (
                <div>
                  <p className={priceLabel}>Product Price</p>
                  <span className="text-xl font-bold text-gray-400 line-through">{currencySymbol}{price.toFixed(2)}</span>
                </div>
              )}
              <div>
                <p className={priceLabel}>{hasDiscount ? "Discount Price" : "Product Price"}</p>
                <span className="text-xl font-bold text-[#095228]">{currencySymbol}{discountPrice.toFixed(2)}</span>
              </div>
              {hasDiscount && (
                <div>
                  <p className={priceLabel}>Discount Percentage</p>
                  <span className="text-xl font-bold text-[#161F28]">{discountPercentage}%</span>
                </div>
              )}
            </div>

            <p className="text-sm leading-6 text-gray-700 text-justify whitespace-pre-line mb-5">
              {product.description || "No product description available."}
            </p>

            <p className={`text-sm font-semibold mb-6 ${canBuy ? "text-[#095228]" : "text-red-600"}`}>
              {!isAvailable
                ? "This product is no longer available"
                : !sellerAcceptingOrders
                ? "This product is currently unavailable"
                : stock > 0
                ? `${stock} ${stock === 1 ? "pc is" : "pcs are"} available`
                : "Out of stock"}
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-3 border border-[#161F28] rounded-full px-2 py-1.5">
                <button
                  type="button"
                  aria-label="Increase quantity"
                  disabled={!canBuy || quantity >= maxQuantity}
                  onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
                  className="w-7 h-7 border border-[#161F28] rounded-full flex items-center justify-center disabled:opacity-40"
                >
                  <IoAdd />
                </button>
                <span className="w-6 text-center font-medium">{String(quantity).padStart(2, "0")}</span>
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  disabled={!canBuy || quantity <= 1}
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="w-7 h-7 border border-[#161F28] rounded-full flex items-center justify-center disabled:opacity-40"
                >
                  <IoRemove />
                </button>
              </div>
              <button
                type="button"
                onClick={handleBuyNow}
                disabled={!canBuy || adding}
                className="flex gap-2 items-center bg-[#161F28] text-white rounded-full px-8 py-3 text-xs font-semibold shadow-lg hover:bg-[#1B2537]/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <IoBagHandleOutline className="text-lg" /> <span>{adding ? "ADDING..." : "BUY NOW"}</span>
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProductView;
